// DurationChipGroup — preset duration chips + optional Custom chip that
// reveals an inline DurationField.
// design ref: styles.css:1687–1698 (.duration-chips / .duration-chip) and
// styles.css:2872–2898 (.ra-dchips / .ra-dchip / .ra-dchip--active /
// .ra-dchip--custom / .ra-dchips__picker). We follow the newer `.ra-dchip`
// palette (azurite-100/300/800) — it's the on-brand soft-tinted active
// state, not the inverted blue of the older `.duration-chip.active`.
//
// JSX pattern from bulk-request.jsx:283–337 — a chip is `active` iff it
// matches `value` *and* the custom picker is not open; the Custom chip is
// active when the value doesn't match any preset (or the user explicitly
// opened it).

import { Box, chakra } from "@chakra-ui/react";
import { useState } from "react";
import { TbClock } from "react-icons/tb";
import DurationField, { DurationValue, DurationUnit } from "./DurationField";
import { durationLabel } from "./durationLabel";

export interface DurationPreset {
  /** Human label shown in the chip, e.g. "1 day", "3 months". */
  label: string;
  value: DurationValue;
}

export interface DurationChipGroupProps {
  presets: DurationPreset[];
  /** The current duration. `null` = nothing selected; defaults to first preset. */
  value: DurationValue | null;
  onChange: (next: DurationValue) => void;
  /** When true, render a "Custom" chip that opens an inline number+unit picker. */
  allowCustom?: boolean;
  /** When true, render an "Indefinite" chip (no expiry) — resource-gated. */
  allowIndefinite?: boolean;
}

// `.ra-dchip` — display: inline-flex; align-items: center; gap: 6px;
// padding 6×12; border 1px solid neutral-200; radius 100px; bg neutral-0;
// font 13/500; color neutral-800; transitions on bg/border/color.
const ChipButton = chakra("button", {
  baseStyle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "1xs", // 6px
    px: "md", // 12px
    py: "1xs", // 6px
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "full", // 100px
    fontFamily: "inherit",
    fontSize: "smd", // 13px
    fontWeight: 500,
    cursor: "pointer",
    transition: "background-color 120ms, border-color 120ms, color 120ms",
    _focusVisible: {
      outline: "none",
      borderColor: "p.400",
      boxShadow: "focusRing",
    },
  },
});

const INACTIVE = {
  bg: "neutral.0",
  borderColor: "neutral.200",
  color: "neutral.800",
  _hover: {
    bg: "neutral.50",
    borderColor: "neutral.300",
  },
};

const ACTIVE = {
  bg: "p.50",
  borderColor: "p.300",
  color: "p.800",
  _hover: {
    bg: "p.50",
    borderColor: "p.400",
  },
};

const sameValue = (a: DurationValue | null, b: DurationValue): boolean =>
  !!a && a.num === b.num && a.unit === b.unit;

const DurationChipGroup = ({
  presets,
  value,
  onChange,
  allowCustom = true,
  allowIndefinite = false,
}: DurationChipGroupProps) => {
  const indefinite = Boolean(value?.indefinite);
  const matchIdx =
    value && !indefinite
      ? presets.findIndex((p) => sameValue(value, p.value))
      : -1;

  // Custom picker is open if (a) user explicitly opened it, or (b) we have
  // a value that doesn't match any preset. Indefinite is its own chip, never
  // "custom".
  const [customOpen, setCustomOpen] = useState(false);
  const customActive =
    allowCustom && !indefinite && (customOpen || (!!value && matchIdx === -1));

  // Fallback value for the inline picker when there's no current value.
  const pickerValue: DurationValue = value ??
    presets[0]?.value ?? { num: 1, unit: "days" as DurationUnit };

  return (
    <Box>
      <Box
        display="flex"
        flexWrap="wrap"
        gap="sm" // 8px (spec); design uses 6px on .ra-dchips, 8px on .duration-chips. 8 matches the brief.
        role="radiogroup"
        aria-label="Duration"
      >
        {presets.map((p, i) => {
          const active = !customActive && matchIdx === i;
          const style = active ? ACTIVE : INACTIVE;
          return (
            <ChipButton
              key={p.label}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => {
                onChange(p.value);
                setCustomOpen(false);
              }}
              {...style}
            >
              {p.label}
            </ChipButton>
          );
        })}
        {allowCustom && (
          <ChipButton
            type="button"
            role="radio"
            aria-checked={customActive}
            onClick={() => {
              // Leaving indefinite for a finite custom duration: clear the flag.
              if (indefinite)
                onChange({
                  num: value?.num ?? 1,
                  unit: value?.unit ?? ("days" as DurationUnit),
                  indefinite: false,
                });
              setCustomOpen(true);
            }}
            {...(customActive ? ACTIVE : INACTIVE)}
          >
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              fontSize="md" // 14px — matches `.ra-dchip--custom .ti`
              color={customActive ? "p.700" : "neutral.600"}
              aria-hidden
            >
              <TbClock size={14} />
            </Box>
            {customActive && matchIdx === -1 && value
              ? durationLabel(value)
              : "Custom"}
          </ChipButton>
        )}
        {allowIndefinite && (
          <ChipButton
            type="button"
            role="radio"
            aria-checked={indefinite}
            onClick={() => {
              onChange({
                num: value?.num ?? 1,
                unit: value?.unit ?? ("days" as DurationUnit),
                indefinite: true,
              });
              setCustomOpen(false);
            }}
            {...(indefinite ? ACTIVE : INACTIVE)}
          >
            Indefinite
          </ChipButton>
        )}
      </Box>

      {customActive && (
        // `.ra-dchips__picker` — margin-top 10px, padding-bottom 4px.
        <Box mt="2.5" pb="2xs">
          <DurationField
            value={pickerValue}
            onChange={(v) => {
              onChange(v);
              // Keep the custom picker open while editing.
              setCustomOpen(true);
            }}
          />
        </Box>
      )}
    </Box>
  );
};

DurationChipGroup.displayName = "DurationChipGroup";

export default DurationChipGroup;
